import type {
  ApiBlobResource,
  ApiReturnResource,
  ApiReturnTag,
} from '@highimpact-frontend/api-types';
import {
  CustomizedDialog,
  FilePicker,
  TagInput,
} from '@highimpact/dashboard-ui';
import { Box, Button, Stack } from '@mui/material';
import type { LegacyRef } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useDashboardHost } from '../../../../../context/DashboardHostContext';
import {
  createValidator,
  useForm,
} from '../../../../../internal/hooks/useForm';
import { includeByName } from '../../../../../internal/utils/functions';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../../runtime/DashboardLibraryProvider';
import { persistImageWidgetResourceAssignment } from '../assignDashboardElementResource';
import type { ImageWidgetProps } from './ImageWidget';

export interface CreateImageResourceProps {
  isModalOpen: boolean;
  handleModalOpen: () => void;
  project_id: number;
  site_id: number;
  grid_type: 'reports' | 'grids';
  afterCreate?: () => void;
}

function CreateImageResource(
  {
    isModalOpen,
    handleModalOpen,
    project_id,
    site_id,
    grid_type,
    afterCreate,
  }: CreateImageResourceProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const host = useDashboardHost();
  const [selectedTags, setSelectedTagsIds] = useState<ApiReturnTag[]>([]);
  const [projectTags, setProjectTags] = useState<ApiReturnTag[]>([]);
  const [globalTags, setGlobalTags] = useState<ApiReturnTag[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const config = useDashboardLibrarySelector(
    (state) => state.dashboard.resourceSelectorConfig
  );

  const dispatch = useDashboardLibraryDispatch();

  const widgetContext = useDashboardLibrarySelector((state) => {
    if (config.state !== 'open') {
      return undefined;
    }

    if (!state.dashboard[grid_type][config.dashboardId]) {
      return undefined;
    }

    const widget =
      state.dashboard[grid_type][config.dashboardId].widgets[config.widgetId];

    if (!widget) {
      return undefined;
    }

    return {
      widgetId: config.widgetId,
      gridId: config.dashboardId,
      dashboardElementId: widget.dashboardElementId,
      elementData: widget.elementData,
      currentResourceId: (widget.props as ImageWidgetProps | undefined)
        ?.resource_id,
    };
  });

  const processAddTags = async (
    newTags: ApiReturnTag[],
    resource: ApiReturnResource
  ): Promise<void> => {
    await Promise.all(
      newTags.map(async (newTag) => {
        if (!resource) {
          return Promise.resolve();
        }
        if (!includeByName(newTag, globalTags)) {
          await host.api.tag.add(newTag);
          await host.api.tag.associateTagToProject(
            [newTag.name],
            resource.project_id
          );
        }
        await host.api.tag.associateTagToResource(resource.id, [newTag.name]);
      })
    );
  };

  const onSubmit = async (data: ApiBlobResource) => {
    try {
      setIsUploading(true);
      const selectedImage = await host.api.resource.add(data);
      if (selectedImage) {
        if (
          config.state === 'open' &&
          widgetContext?.dashboardElementId &&
          widgetContext.elementData
        ) {
          await persistImageWidgetResourceAssignment(
            host.api.dashboard,
            dispatch,
            {
              gridType: config.grid_type,
              gridId: widgetContext.gridId,
              widgetId: widgetContext.widgetId,
              dashboardElementId: widgetContext.dashboardElementId,
              resourceId: selectedImage.id,
              elementData: widgetContext.elementData,
            }
          );
        }
        await processAddTags(selectedTags, selectedImage);
      }
      setIsUploading(false);
      host.showNotification('La ressource a été créée avec succès', 'success');
      afterCreate && afterCreate();
      handleModalOpen();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      let msg =
        error.response?.data?.message ||
        'Une erreur est survenue lors de la création de la ressource.';
      if (
        error.response?.data?.detail?.includes(
          'duplicate key value violates unique constraint'
        )
      ) {
        msg = 'Création impossible, le nom est déjà utilisé';
      }
      host.showNotification(msg, 'error');
      console.error('Error:', error);
      setIsUploading(false);
    }
  };

  const defaultValues: ApiBlobResource = {
    project_id,
    site_id,
    name: '',
    type: 'image',
    content_type: '',
    resource_type: 'blob',
    file: null,
  };

  const validator = createValidator<ApiBlobResource>({
    project_id: [],
    site_id: [],
    name: [(n) => (n.length <= 3 ? 'Entrer au moins 4 caractères' : undefined)],
    type: [],
    content_type: [],
    resource_type: [],
    file: [],
  });

  const methods = useForm(defaultValues, validator, onSubmit);

  const handleSelectedTags = (values: ApiReturnTag[]) => {
    setSelectedTagsIds(values);
  };

  useEffect(() => {
    host.api.tag.getAll().then((response) => setGlobalTags(response));
    host.api.tag
      .getAllFromProject(project_id)
      .then((response) => setProjectTags(response));
  }, [host.api.tag, project_id]);

  return (
    <CustomizedDialog
      open={isModalOpen}
      onCancel={handleModalOpen}
      title="Créer une ressource"
      dialogMaxWidth="480px"
    >
      <div ref={ref}>
        <Box component="form" onSubmit={methods.onSubmit}>
          <Stack spacing={2}>
            <FilePicker
              filehandler={(file) => {
                methods.setValue('file', file as File);
              }}
              namehandler={(value: string) => methods.setValue('name', value)}
            />
            {/* FIXME: the role of predicate should be clarified and/or refactored */}
            <TagInput
              predicate={(substring) => (data) =>
                Object.values(data).some((value) =>
                  String(value)
                    .toLowerCase()
                    .includes(substring[0]!.toLowerCase())
                )}
              selectionValues={
                projectTags
                  ? (projectTags as {
                      id: number;
                      name: string;
                      is_generic: boolean;
                    }[])
                  : []
              }
              handleSelectedValues={handleSelectedTags}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={
                methods.watch('name').length < 4 ||
                methods.watch('file') === null ||
                selectedTags.length === 0 ||
                isUploading
              }
              fullWidth
            >
              Enregistrer
            </Button>
          </Stack>
        </Box>
      </div>
    </CustomizedDialog>
  );
}

export default forwardRef(CreateImageResource);
